import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const ValuesSection = () => {
  const coreValues = [
    {
      icon: "Shield",
      title: "Confiance & Intégrité",
      description: "Nous bâtissons des relations durables grâce à une communication transparente, des pratiques commerciales éthiques et un engagement inébranlable envers nos promesses. Chaque interaction reflète notre dévouement à gagner et maintenir la confiance des clients."
    },
    {
      icon: "Target",
      title: "Excellence dans la Livraison",
      description: "Nous établissons les normes les plus élevées de qualité de service dans les 12 secteurs d'activité. Notre engagement envers l'excellence stimule l'amélioration continue et garantit des résultats constants et supérieurs pour chaque engagement client."
    },
    {
      icon: "Users",
      title: "Approche Centrée Client",
      description: "Le succès de nos clients est notre succès. Nous écoutons, comprenons et adaptons nos solutions complètes pour répondre aux besoins commerciaux uniques, garantissant que chaque service apporte une valeur mesurable et dépasse les attentes."
    },
    {
      icon: "Lightbulb",
      title: "Innovation & Adaptation",
      description: "Nous embrassons le changement et exploitons la technologie pour rester en avance sur le marché dynamique de la RDC. Notre approche innovante combine les meilleures pratiques internationales avec les connaissances du marché local pour des solutions optimales."
    },
    {
      icon: "Heart",
      title: "Impact Communautaire",
      description: "Nous nous engageons à contribuer positivement à l'économie et à la société de la RDC. Par des pratiques commerciales responsables, l'emploi local et des initiatives communautaires, nous aidons à construire une nation plus forte et plus prospère."
    },
    {
      icon: "Zap",
      title: "Fiabilité & Cohérence",
      description: "Nous tenons nos engagements, à chaque fois. Notre taux de fidélisation client de 98% reflète notre performance constante et notre prestation de services fiable dans toutes les opérations commerciales et interactions clients."
    }
  ];

  return (
    <section className="py-20 lg:py-32 bg-slate-100 dark:bg-slate-900 relative overflow-hidden border-t border-slate-200 dark:border-white/5">
      {/* Radial gradient background */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-slate-800 via-slate-900 to-black opacity-0 dark:opacity-80" />

      {/* Noise texture overlay */}
      <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E\")", backgroundRepeat: "repeat" }} />

      <div className="w-full max-w-[1920px] mx-auto px-6 sm:px-12 lg:px-16 relative z-10">
        {/* Section header */}
        <motion.div
          className="text-center mb-12 lg:mb-20"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
        >
          <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/20 rounded-full px-5 py-2.5 mb-6">
            <Icon name="Compass" size={18} className="text-blue-400" />
            <span className="text-sm font-medium text-blue-600 dark:text-blue-300">Nos Valeurs Fondamentales</span>
          </div>
          <h2 className="text-4xl md:text-6xl font-bold text-slate-900 dark:text-white mb-6">
            Principes Qui Guident{' '}
            <span className="bg-gradient-to-r from-blue-400 to-amber-400 bg-clip-text text-transparent">
              Tout Ce Que Nous Faisons
            </span>
          </h2>
          <p className="text-lg text-slate-600 dark:text-slate-400 max-w-3xl mx-auto leading-relaxed">
            Nos valeurs ne sont pas que des mots sur une page—elles sont le fondement de chaque décision, chaque service et chaque relation client que nous construisons.
          </p>
        </motion.div>

        {/* Values grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {coreValues.map((value, index) => (
            <motion.div
              key={index}
              className="bg-white/80 dark:bg-slate-800/40 backdrop-blur-md border border-slate-200 dark:border-white/10 rounded-3xl p-10 text-center hover:border-blue-500/30 hover:bg-slate-100 dark:hover:bg-slate-800/60 transition-all duration-300 shadow-xl group"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <div className="w-20 h-20 mx-auto mb-8 rounded-2xl bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center shadow-lg shadow-blue-900/30 group-hover:scale-110 transition-transform duration-300">
                <Icon name={value.icon} size={32} className="text-white" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-4">
                {value.title}
              </h3>
              <p className="text-base text-slate-600 dark:text-slate-400 leading-relaxed">
                {value.description}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Quote block */}
        <motion.div
          className="mt-16 lg:mt-24 bg-white/80 dark:bg-slate-800/60 backdrop-blur-xl border border-slate-200 dark:border-white/10 rounded-[2.5rem] p-10 md:p-16 text-center"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7, delay: 0.2 }}
        >
          <Icon name="Quote" size={48} className="mx-auto mb-8 text-blue-500/30" />
          <blockquote className="text-xl md:text-2xl lg:text-3xl font-semibold text-slate-700 dark:text-slate-200 mb-6 leading-relaxed max-w-4xl mx-auto">
            "Notre mission est d'être le fournisseur de solutions complètes le plus fiable en République Démocratique du Congo, offrant l'excellence dans chaque secteur d'activité tout en contribuant à la croissance économique et à la prospérité de la nation."
          </blockquote>
          <p className="text-base text-slate-500 dark:text-slate-400">— Jean-Pierre Mukendi, Fondateur & PDG</p>
        </motion.div>
      </div>
    </section>
  );
};

export default ValuesSection;
